import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useLeads } from '../../../context/LeadContext';
import { get } from '../../../api/apiClient';
import {
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  MapPin,
  Calendar,
  Clock,
  Target,
  CheckCircle,
  AlertCircle,
  X,
  Plus,
  Users,
  Phone,
  Mail,
  Building,
  Trash2,
  FileText,
  RefreshCw,
  Upload,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  UserPlus,
  ArrowRight
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
  handleSearchInputChange,
  FIELD_LIMITS,
} from '../../../utils/inputValidation';
import { toast, Toaster } from 'react-hot-toast';
import leadScoringService from '../../../api/crm/leads/leadScoringService';
import leadService from '../../../api/crm/leads/leadService';
import assignmentRuleService from '../../../api/crm/assignment-rules/assignmentRuleService';
import { API_ENDPOINTS } from '../../../api/config';
import { useAuth } from '../../../context/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { USER_ROLES, getLeadStatusColor } from '../../../utils/constants';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import BulkUploadModal from '../../../components/common/BulkUploadModal';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import AssignmentRuleModal from './AssignmentRuleModal';
import ScoringRuleModal from './ScoringRuleModal';
import scoringRuleService from '../../../api/crm/leads/scoringRuleService';
import QualificationCriteriaModal from './QualificationCriteriaModal';
import qualificationCriteriaService from '../../../api/crm/leads/qualificationCriteriaService';
import ConvertLeadModal from './ConvertLeadModal';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

const LeadListFilters = ({ onViewLead, onEditLead, onAssignLead, onAddLead }) => {
  // Constants for bulk upload instructions
  const COMPANY_SIZES = [
    'Startup (1-10 employees)',
    'Small (10-50 employees)',
    'Medium (50-200 employees)',
    'Large (200-1000 employees)',
    'Enterprise (1000+ employees)'
  ];

  const REGIONS = ['North', 'South', 'East', 'West', 'Central', 'North-East', 'North-West', 'South-East', 'South-West'];

  const TIMELINES = ['Immediate (0-30 days)', 'Short-term (1-3 months)', 'Medium-term (3-6 months)', 'Long-term (6+ months)'];


  const navigate = useNavigate();
  const { user } = useAuth();
  const isSalesManager = user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER;
  const isSalesExecutive = user?.role === USER_ROLES.SALES_EXECUTIVE || user?.role === USER_ROLES.EXPORT_EXECUTIVE || user?.role === USER_ROLES.BUSINESS_RELATIONSHIP_MANAGER || user?.role === USER_ROLES.BUSINESS_DEVELOPMENT_EXECUTIVE;
  const isSalesScopedUser = isSalesManager || isSalesExecutive;
  // IMPORTANT: In auth payload, `id` is User._id while `_id` may be Employee._id.
  // Lead.assignedTo references User._id, so always prefer `id` for assigned filters.
  const currentUserId =
    user?.id ||
    user?.userId ||
    (() => {
      try {
        const persisted = JSON.parse(localStorage.getItem('user_data') || '{}');
        return persisted?.id || persisted?.userId || persisted?._id;
      } catch (e) {
        return user?._id;
      }
    })();

  // Check if user has permission to run auto qualification (only super admin and sales manager)
  const canModify = user?.role === USER_ROLES.SUPER_ADMIN || isSalesManager;

  // Permission-based access control - use granular permissions instead of role checks
  // This allows custom permission assignments beyond just roles
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const { leads, deleteLead, bulkDeleteLeads, bulkAssignLeads, loading, error, loadLeads, pagination, clearError, mapStageToStatusName } = useLeads();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get('limit')) || 10);
  const [sortColumn, setSortColumn] = useState(searchParams.get('sortBy') || 'updatedAt');
  const [sortDirection, setSortDirection] = useState(searchParams.get('sortOrder') || 'desc');
  const [filters, setFilters] = useState({
    source: searchParams.get('source') || location.state?.filters?.source || 'all',
    status: searchParams.get('status') || location.state?.filters?.status || 'all',
    score: searchParams.get('score') || location.state?.filters?.score || 'all',
    owner: searchParams.get('owner') || location.state?.filters?.owner || 'all',
    assignee: searchParams.get('assignee') || location.state?.filters?.assignee || 'all',
    region: searchParams.get('region') || location.state?.filters?.region || 'all',
    companySource: searchParams.get('companySource') || location.state?.filters?.companySource || 'all',
    startDate: searchParams.get('startDate') || location.state?.filters?.startDate || '',
    endDate: searchParams.get('endDate') || location.state?.filters?.endDate || '',
    leadVideoStatus: searchParams.get('leadVideoStatus') || location.state?.filters?.leadVideoStatus || 'all',
    leadOrderStatus: searchParams.get('leadOrderStatus') || location.state?.filters?.leadOrderStatus || 'all',
    leadCallStatus: searchParams.get('leadCallStatus') || location.state?.filters?.leadCallStatus || 'all',
    leadInterest: searchParams.get('leadInterest') || location.state?.filters?.leadInterest || 'all',
    duplicateFilter: searchParams.get('duplicateFilter') || location.state?.filters?.duplicateFilter || 'all'
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = [
        'search', 'source', 'status', 'score', 'owner', 'assignee', 'region',
        'companySource', 'startDate', 'endDate', 'leadVideoStatus',
        'leadOrderStatus', 'leadCallStatus', 'leadInterest', 'page', 'limit',
        'sortBy', 'sortOrder', 'duplicateFilter'
      ];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (filters.source && filters.source !== 'all') params.set('source', filters.source);
      if (filters.status && filters.status !== 'all') params.set('status', filters.status);
      if (filters.score && filters.score !== 'all') params.set('score', filters.score);
      if (filters.owner && filters.owner !== 'all') params.set('owner', filters.owner);
      if (filters.assignee && filters.assignee !== 'all') params.set('assignee', filters.assignee);
      if (filters.region && filters.region !== 'all') params.set('region', filters.region);
      if (filters.companySource && filters.companySource !== 'all') params.set('companySource', filters.companySource);
      if (filters.startDate) params.set('startDate', filters.startDate);
      if (filters.endDate) params.set('endDate', filters.endDate);
      if (filters.leadVideoStatus && filters.leadVideoStatus !== 'all') params.set('leadVideoStatus', filters.leadVideoStatus);
      if (filters.leadOrderStatus && filters.leadOrderStatus !== 'all') params.set('leadOrderStatus', filters.leadOrderStatus);
      if (filters.leadCallStatus && filters.leadCallStatus !== 'all') params.set('leadCallStatus', filters.leadCallStatus);
      if (filters.leadInterest && filters.leadInterest !== 'all') params.set('leadInterest', filters.leadInterest);
      if (filters.duplicateFilter && filters.duplicateFilter !== 'all') params.set('duplicateFilter', filters.duplicateFilter);

      if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortColumn && sortColumn !== 'updatedAt') params.set('sortBy', sortColumn);
      if (sortDirection && sortDirection !== 'desc') params.set('sortOrder', sortDirection);

      return params;
    }, { replace: true });
  }, [
    debouncedSearchTerm,
    filters.source,
    filters.status,
    filters.score,
    filters.owner,
    filters.assignee,
    filters.region,
    filters.companySource,
    filters.startDate,
    filters.endDate,
    filters.leadVideoStatus,
    filters.leadOrderStatus,
    filters.leadCallStatus,
    filters.leadInterest,
    filters.duplicateFilter,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortDirection,
    setSearchParams
  ]);

  // State for assignee filter options
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [filteredAssigneeOptions, setFilteredAssigneeOptions] = useState([]);
  const [openAssigneeDropdown, setOpenAssigneeDropdown] = useState(false);
  const [assigneeDropdownPosition, setAssigneeDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const assigneeDropdownRef = useRef(null);

  const getAssignedToFilter = () => {
    if (isSalesExecutive) {
      return currentUserId || undefined;
    }
    if (isSalesManager) {
      return filters.assignee !== 'all' ? (filters.assignee === 'Unassigned' ? 'unassigned' : filters.assignee) : undefined;
    }
    return filters.owner !== 'all' ? (filters.owner === 'Unassigned' ? 'unassigned' : filters.owner) : undefined;
  };

  const buildLeadApiFilters = (overrides = {}) => {
    const apiFilters = {
      page: currentPage,
      limit: itemsPerPage,
      search: searchTerm || undefined,
      source: filters.source !== 'all' ? mapSourceToBackend(filters.source) : undefined,
      stage: filters.status !== 'all' ? mapStatusToBackend(filters.status) : undefined,
      scoreLabel: filters.score !== 'all' ? filters.score.toLowerCase() : undefined,
      // Enforce self-scope for sales roles regardless of UI filter state.
      assignedTo: isSalesExecutive
        ? (currentUserId || undefined)
        : (filters.assignee !== 'all'
          ? (filters.assignee === 'Unassigned' ? 'unassigned' : filters.assignee)
          : (isSalesManager ? undefined : getAssignedToFilter())),
      region: filters.region !== 'all' ? filters.region : undefined,
      companySource: filters.companySource !== 'all' ? filters.companySource : undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      leadVideoStatus: filters.leadVideoStatus !== 'all' ? filters.leadVideoStatus : undefined,
      leadOrderStatus: filters.leadOrderStatus !== 'all' ? filters.leadOrderStatus : undefined,
      leadCallStatus: filters.leadCallStatus !== 'all' ? filters.leadCallStatus : undefined,
      leadInterest: filters.leadInterest !== 'all' ? filters.leadInterest : undefined,
      duplicateFilter: filters.duplicateFilter !== 'all' ? filters.duplicateFilter : undefined,
      ...overrides
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) {
        delete apiFilters[key];
      }
    });

    return apiFilters;
  };

  const [masterSources, setMasterSources] = useState([]);
  const [masterLeadStatuses, setMasterLeadStatuses] = useState([]);
  const masterSourcesRef = useRef([]);
  const [masterDocumentTypes, setMasterDocumentTypes] = useState([]);
  const [masterIndustries, setMasterIndustries] = useState([]);
  const [masterDecisionMakers, setMasterDecisionMakers] = useState([]);
  const [companySources, setCompanySources] = useState([]);
  const [masterVideoStatuses, setMasterVideoStatuses] = useState([]);
  const [masterOrderStatuses, setMasterOrderStatuses] = useState([]);
  const [masterCallStatuses, setMasterCallStatuses] = useState([]);
  const [masterInterests, setMasterInterests] = useState([]);
  const [masterCompanySizes, setMasterCompanySizes] = useState([]);
  const [masterCategories, setMasterCategories] = useState([]);

  // Sorting state (moved up)

  // Selection state
  const [selectedRows, setSelectedRows] = useState(new Set());
  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  // State for Assignment rule modal
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [users, setUsers] = useState([]); // All users
  const [filteredUsers, setFilteredUsers] = useState([]); // Search results for rule modal dropdown

  // Scoring Modal State
  const [showScoringModal, setShowScoringModal] = useState(false);
  const [selectedScoring, setSelectedScoring] = useState(null);
  const [isSavingScoring, setIsSavingScoring] = useState(false);
  const [scoringErrors, setScoringErrors] = useState({});
  const [scoringFormData, setScoringFormData] = useState({
    name: '',
    description: '',
    rules: [],
    isActive: true
  });

  const handleSaveScoring = async () => {
    // Basic validation
    const errors = {};
    if (!scoringFormData.name.trim()) errors.name = 'Rule name is required.';
    // Add other validations as needed, similar to LeadScoringQualification
    // For brevity, using basic check or delegating to modal if it handles it (it handles it via props if we pass errors back)
    // Actually the modal relies on us to validat and set errors usually, or it does internal validation.
    // In LeadScoringQualification, handleSaveScoring does the validation.
    // Let's implement minimal validation here or reuse a validator if extracted.

    // Checks from LeadScoringQualification
    if (scoringFormData.name.trim().length === 0) errors.name = "Rule name is required";
    if (scoringFormData.rules.length === 0) errors.rules = "At least one rule is required";

    const totalScore = scoringFormData.rules.reduce((sum, rule) => sum + Number(rule.score), 0);
    // if (totalScore !== 100) errors.rules = `Total score must equal 100 (Current: ${totalScore})`; // Optional check

    if (Object.keys(errors).length > 0) {
      setScoringErrors(errors);
      return;
    }

    setIsSavingScoring(true);
    try {
      // Transform rules to match backend format
      const rulesToSave = scoringFormData.rules.map(rule => ({
        field: rule.field,
        operator: rule.operator || 'equals',
        value: rule.condition || rule.value,
        score: rule.score
      }));

      const ruleData = {
        ...scoringFormData,
        rules: rulesToSave
      };

      const result = await scoringRuleService.createScoringRule(ruleData); // Only create mode here? Or edit too? Assuming create for now based on button "Assignment Rule" pattern.

      if (result.success) {
        toast.success('Scoring rule created successfully!');
        setScoringFormData({
          name: '',
          description: '',
          rules: [],
          isActive: true
        });
        setSelectedScoring(null);
        setShowScoringModal(false);
      } else {
        toast.error(result.error || 'Failed to create scoring rule');
      }
    } catch (error) {
      toast.error('Failed to save scoring rule');
    } finally {
      setIsSavingScoring(false);
    }
  };

  // Criteria Modal State
  const [showCriteriaModal, setShowCriteriaModal] = useState(false);
  const [selectedCriteria, setSelectedCriteria] = useState(null);
  const [isSavingCriteria, setIsSavingCriteria] = useState(false);
  const [criteriaErrors, setCriteriaErrors] = useState({});
  const [criteriaFormData, setCriteriaFormData] = useState({
    name: '',
    description: '',
    criteria: [],
    isActive: true,
    qualificationLogic: 'all',
    qualificationResult: 'qualified',
    leadTemperature: 'Cold'
  });

  const handleSaveCriteria = async () => {
    // Basic validation
    const newErrors = {};
    if (!criteriaFormData.name.trim()) newErrors.name = 'Criteria name is required.';
    if (criteriaFormData.criteria.length === 0) newErrors.criteria = 'Please add at least one qualifying criterion.';

    if (Object.keys(newErrors).length > 0) {
      setCriteriaErrors(newErrors);
      return;
    }

    setIsSavingCriteria(true);

    try {
      // Clean up data before sending
      const criteriaToSave = {
        ...criteriaFormData,
        // Ensure numeric fields if any? (Not needed for current fields)
        // Map 'role' to 'designation' if needed inside criteria array?
        criteria: criteriaFormData.criteria.map(c => ({
          ...c,
          field: c.field === 'role' ? 'designation' : c.field
        }))
      };

      let result;
      if (selectedCriteria) {
        result = await qualificationCriteriaService.updateQualificationCriteria(selectedCriteria._id || selectedCriteria.id, criteriaToSave);
      } else {
        result = await qualificationCriteriaService.createQualificationCriteria(criteriaToSave);
      }

      if (result.success) {
        toast.success(selectedCriteria ? 'Criteria updated successfully' : 'Criteria created successfully');
        setCriteriaFormData({
          name: '',
          description: '',
          criteria: [],
          isActive: true,
          qualificationLogic: 'all',
          qualificationResult: 'qualified',
          leadTemperature: 'Cold'
        });
        setSelectedCriteria(null);
        setShowCriteriaModal(false);
      } else {
        toast.error(result.error || 'Failed to save criteria');
      }
    } catch (error) {
      toast.error('Failed to save criteria');
    } finally {
      setIsSavingCriteria(false);
    }
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted leads
  const getSortedLeads = () => {
    const rawLeads = leads || [];
    if (!sortColumn || rawLeads.length === 0 || filters.duplicateFilter === 'duplicates') return rawLeads;

    return [...rawLeads].sort((a, b) => {
      let aValue, bValue;

      if (sortColumn === 'leadId') {
        aValue = a.leadId || '';
        bValue = b.leadId || '';
      } else if (sortColumn === 'leadDetails') {
        aValue = getLeadName(a).toLowerCase();
        bValue = getLeadName(b).toLowerCase();
      } else if (sortColumn === 'source') {
        aValue = (a.source || '').toLowerCase();
        bValue = (b.source || '').toLowerCase();
      } else if (sortColumn === 'companySource') {
        aValue = (a.companySource || 'agrosaf').toLowerCase();
        bValue = (b.companySource || 'agrosaf').toLowerCase();
      } else if (sortColumn === 'score') {
        aValue = Number(a.score) || 0;
        bValue = Number(b.score) || 0;
      } else if (sortColumn === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      } else if (sortColumn === 'owner') {
        aValue = (a.owner || '').toLowerCase();
        bValue = (b.owner || '').toLowerCase();
      } else if (sortColumn === 'value') {
        aValue = Number(a.leadValue) || 0;
        bValue = Number(b.leadValue) || 0;
      } else if (sortColumn === 'updatedAt') {
        aValue = new Date(a.updatedAt || a.createdAt || 0).getTime();
        bValue = new Date(b.updatedAt || b.createdAt || 0).getTime();
      } else if (sortColumn === 'lastActivity') {
        aValue = a.lastActivityDate ? new Date(a.lastActivityDate).getTime() : 0;
        bValue = b.lastActivityDate ? new Date(b.lastActivityDate).getTime() : 0;
      } else if (sortColumn === 'scoreLabel') {
        const order = { 'hot': 1, 'warm': 2, 'cold': 3 };
        aValue = order[(a.scoreLabel || 'cold').toLowerCase()] || 4;
        bValue = order[(b.scoreLabel || 'cold').toLowerCase()] || 4;
      } else if (sortColumn === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else {
        aValue = a[sortColumn] || '';
        bValue = b[sortColumn] || '';
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // State for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);

  // State for auto qualification
  const [isRunningAutoQualification, setIsRunningAutoQualification] = useState(false);
  const [autoQualificationProgress, setAutoQualificationProgress] = useState({ processed: 0, total: 0, qualified: 0 });
  const [showAutoQualificationModal, setShowAutoQualificationModal] = useState(false);

  // State for auto assignment
  const [isRunningAutoAssignment, setIsRunningAutoAssignment] = useState(false);
  const [showAutoAssignmentModal, setShowAutoAssignmentModal] = useState(false);

  // State for bulk delete
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);

  // State for bulk upload modal
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  // State for lead assignment dropdowns
  const [openDropdownLeadId, setOpenDropdownLeadId] = useState(null);
  const [leadSearchTerms, setLeadSearchTerms] = useState({});
  const [leadFilteredUsers, setLeadFilteredUsers] = useState({});
  const [leadAllUsers, setLeadAllUsers] = useState({});
  const [dropdownPositions, setDropdownPositions] = useState({});
  const leadDropdownRefs = useRef({});
  const [loadingUsers, setLoadingUsers] = useState(false);

  // State for bulk assign modal
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssignee, setBulkAssignee] = useState(null);
  const [bulkAssignUsers, setBulkAssignUsers] = useState([]);
  const [bulkAssignSearchTerm, setBulkAssignSearchTerm] = useState('');

  const [isAssigningBulk, setIsAssigningBulk] = useState(false);

  // Conversion State
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedLeadForConversion, setSelectedLeadForConversion] = useState(null);

  // Duplicate Matches State
  const [duplicateModalState, setDuplicateModalState] = useState({ isOpen: false, matches: [], sourceLeadId: null, position: null });

  // Track if this is the initial mount to prevent showing stale data
  const isInitialMount = useRef(true);

  // Ref to maintain focus on search input
  const searchInputRef = useRef(null);

  // Check for toast message from navigation state and display it
  useEffect(() => {
    if (location.state?.toast) {
      const { type, message } = location.state.toast;
      if (type === 'success') {
        toast.success(message);
      } else if (type === 'warning' || type === 'error') {
        toast(message, { icon: '⚠️', style: { background: '#f59e0b', color: '#fff' } });
      }
      // Clear the state to prevent showing toast again on re-render
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Clear any stale errors when component mounts
  // This is a safety measure to prevent showing any lingering errors
  useEffect(() => {
    if (error) {
      // Clear error on mount - we're about to load fresh data anyway
      // The error will be cleared again when loadLeads succeeds
      clearError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Fetch dynamic sources on mount
  useEffect(() => {
    const fetchSources = async () => {
      try {
        const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_SOURCES}`);
        if (result.success) {
          const sources = result.data || [];
          setMasterSources(sources);
          masterSourcesRef.current = sources;
        }
      } catch (error) {
      }
    };

    const fetchLeadDocumentTypes = async () => {
      try {
        const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_DOCUMENT_TYPES}`);
        if (result.success) {
          setMasterDocumentTypes(result.data || []);
        }
      } catch (error) {
      }
    };

    const fetchIndustries = async () => {
      try {
        const result = await get(`${API_ENDPOINTS.CRM.CONFIG.INDUSTRIES}?active=true`);
        if (result.success) {
          setMasterIndustries(result.data || []);
        }
      } catch (error) {
      }
    };

    const fetchDecisionMakers = async () => {
      try {
        const result = await get(`${API_ENDPOINTS.CRM.CONFIG.DECISION_MAKERS}?active=true`);
        if (result.success) {
          setMasterDecisionMakers(result.data || []);
        }
      } catch (error) {
      }
    };

    const fetchCompanySources = async () => {
      try {
        const result = await get(`${API_ENDPOINTS.CRM.CONFIG.COMPANY_SOURCES || '/api/config/company-sources'}`);
        if (result.success) {
          setCompanySources(result.data || []);
        }
      } catch (error) {
      }
    };

    const fetchLeadStatuses = async () => {
      try {
        const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_STATUSES_DROPDOWN}`);
        if (result.success) {
          setMasterLeadStatuses(result.data || []);
        }
      } catch (error) {
      }
    };

    const fetchVideoStatuses = async () => {
      try {
        const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_VIDEO_STATUSES}?active=true`);
        if (result.success) {
          setMasterVideoStatuses(result.data || []);
        }
      } catch (error) {
      }
    };

    const fetchOrderStatuses = async () => {
      try {
        const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_ORDER_STATUSES}?active=true`);
        if (result.success) {
          setMasterOrderStatuses(result.data || []);
        }
      } catch (error) {
      }
    };

    const fetchCallStatuses = async () => {
      try {
        const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_CALL_STATUSES}?active=true`);
        if (result.success) {
          setMasterCallStatuses(result.data || []);
        }
      } catch (error) {
      }
    };

    const fetchInterests = async () => {
      try {
        const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_INTERESTS}?active=true`);
        if (result.success) {
          setMasterInterests(result.data || []);
        }
      } catch (error) {
      }
    };

    const fetchCompanySizes = async () => {
      try {
        const result = await get(`${API_ENDPOINTS.CRM.CONFIG.COMPANY_SIZES}?active=true`);
        if (result.success) {
          setMasterCompanySizes(result.data || []);
        }
      } catch (error) {
      }
    };

    const fetchLeadCategories = async () => {
      try {
        const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_CATEGORIES}?active=true`);
        if (result.success) {
          setMasterCategories(result.data || []);
        }
      } catch (error) {
      }
    };

    fetchSources();
    fetchLeadDocumentTypes();
    fetchIndustries();
    fetchDecisionMakers();
    fetchCompanySources();
    fetchLeadStatuses();
    fetchVideoStatuses();
    fetchOrderStatuses();
    fetchCallStatuses();
    fetchInterests();
    fetchCompanySizes();
    fetchLeadCategories();
  }, []);

  // Autofocus search input on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Load assignee options based on user role
  useEffect(() => {
    const loadAssignees = async () => {
      if (isSalesExecutive) {
        setAssigneeOptions([]);
        return;
      }

      setLoadingAssignees(true);
      try {
        const params = new URLSearchParams();
        params.append('limit', 'all');

        if (user?.role === USER_ROLES.SUPER_ADMIN) {
          params.append('roleFilter', 'sales');
        } else if (isSalesManager) {
          params.append('roleFilter', user?.role === USER_ROLES.EXPORT_MANAGER ? 'export_executive' : 'sales_executive');
          params.append('managerId', user._id || user.id);
        }

        const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await authFetch(url, {
        credentials: 'include',
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        const result = await response.json();

        let exportManagers = [];
        if (user?.role === USER_ROLES.SALES_MANAGER) {
          try {
            const emResponse = await authFetch(`${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}?limit=all&roleFilter=sales`, {
        credentials: 'include',
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              }
            });
            const emResult = await emResponse.json();
            if (emResult.responseCode === 200 && emResult.data?.users) {
              exportManagers = emResult.data.users.filter(u => u.role === USER_ROLES.EXPORT_MANAGER || u.designation === 'Export Manager');
            }
          } catch (e) {
          }
        }

        if (result.responseCode === 200 && result.data?.users) {
          const crmUsers = result.data.users.map(u => ({
            id: u.user || u._id || u.id,
            name: u.name || 'Unknown',
            email: u.email || '',
            role: u.role || u.designation || ''
          }));
          const mappedExportManagers = exportManagers.map(u => ({
            id: u.user || u._id || u.id,
            name: u.name || 'Unknown',
            email: u.email || '',
            role: u.role || u.designation || ''
          }));

          const allUsers = [...crmUsers, ...mappedExportManagers];
          const uniqueUsersMap = new Map();
          allUsers.forEach(u => {
            if (u.id) uniqueUsersMap.set(u.id, u);
          });
          const mergedUsers = Array.from(uniqueUsersMap.values());

          const baseOptions = [
            { id: 'all', name: 'All', email: '', role: '' },
            ...(isSalesManager ? [] : [{ id: 'Unassigned', name: 'Unassigned', email: '', role: '' }]),
            { id: 'all_assigned', name: 'All Assigned', email: '', role: '' }
          ];
          // If the logged-in user is a manager, add them to the list so they can filter their own leads
          const managerOption = (user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER)
            ? [{
              id: currentUserId,
              name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Me',
              email: user?.email || '',
              role: user?.role || ''
            }]
            : [];
          setAssigneeOptions([...baseOptions, ...managerOption, ...mergedUsers]);
          setFilteredAssigneeOptions([...baseOptions, ...managerOption, ...mergedUsers]);
        }
      } catch (error) {
        const defaultOptions = [
          { id: 'all', name: 'All', email: '', role: '' },
          ...(isSalesManager ? [] : [{ id: 'Unassigned', name: 'Unassigned', email: '', role: '' }]),
          { id: 'all_assigned', name: 'All Assigned', email: '', role: '' }
        ];
        setAssigneeOptions(defaultOptions);
        setFilteredAssigneeOptions(defaultOptions);
      } finally {
        setLoadingAssignees(false);
      }
    };

    loadAssignees();
  }, [user]);

  // Debounced search for assignee filter dropdown
  useEffect(() => {
    if (!openAssigneeDropdown) return;
    const handler = setTimeout(() => {
      if (assigneeSearchTerm.trim() === '') {
        setFilteredAssigneeOptions(assigneeOptions);
      } else {
        const searchLower = assigneeSearchTerm.toLowerCase();
        const filtered = assigneeOptions.filter(option =>
          option.name.toLowerCase().includes(searchLower) ||
          option.email.toLowerCase().includes(searchLower) ||
          option.role.toLowerCase().includes(searchLower)
        );
        setFilteredAssigneeOptions(filtered);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [assigneeSearchTerm, openAssigneeDropdown, assigneeOptions]);

  // Calculate assignee dropdown position
  useEffect(() => {
    if (openAssigneeDropdown && assigneeDropdownRef.current) {
      const rect = assigneeDropdownRef.current.getBoundingClientRect();
      setAssigneeDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [openAssigneeDropdown]);

  // Handle click outside assignee dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        const dropdownElement = document.querySelector('[data-dropdown-assignee-filter]');
        if ((dropdownElement && !dropdownElement.contains(event.target)) || !dropdownElement) {
          setOpenAssigneeDropdown(false);
          setAssigneeSearchTerm('');
        }
      }
    };
    if (openAssigneeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openAssigneeDropdown]);

  // Helper function to get lead name
  const getLeadName = (lead) => {
    if (lead.firstName && lead.lastName) {
      return `${lead.firstName} ${lead.lastName}`;
    } else if (lead.firstName) {
      return lead.firstName;
    } else if (lead.lastName) {
      return lead.lastName;
    } else if (lead.name) {
      return lead.name;
    } else {
      return 'Unknown Lead';
    }
  };

  // Helper function to get performer name
  const getPerformerName = (performer) => {
    if (!performer) return 'System';
    if (typeof performer === 'object') {
      return `${performer.firstName || ''} ${performer.lastName || ''}`.trim() || performer.email || 'Unknown';
    }
    return performer;
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Helper function to calculate days since last activity
  const calculateDaysSince = (dateStr) => {
    if (!dateStr) return 'No activity';
    const date = new Date(dateStr);
    const now = new Date();

    // Set to start of day to calculate calendar days difference
    const d1 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const d2 = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const diffTime = d2 - d1;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return 'Today';
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  const filteredLeads = useMemo(() => getSortedLeads(), [leads, sortColumn, sortDirection]);

  const currentPageLeadIds = useMemo(
    () =>
      filteredLeads
        .map((lead) => {
          const raw = lead?._id ?? lead?.id ?? lead?.leadId;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [filteredLeads]
  );

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  };

  // Debounce search to avoid too many API calls (similar to LeadActivities)
  useEffect(() => {
    if (isInitialMount.current) return;

    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      setSelectedRows(new Set()); // Reset selection on search/filter change
    }, 500); // 500ms debounce - same as LeadActivities

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filters.source, filters.status, filters.score, filters.owner, filters.assignee, filters.region, filters.companySource, filters.startDate, filters.endDate, filters.leadVideoStatus, filters.leadOrderStatus, filters.leadCallStatus, filters.leadInterest, filters.duplicateFilter, itemsPerPage]);

  // Load leads from API when filters change (debounced) - single effect to prevent duplicate calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const apiFilters = buildLeadApiFilters();

      // Loading leads with filters
      loadLeads(apiFilters);
    }, isInitialMount.current ? 0 : 500); // Load immediately on mount, debounce on subsequent changes

    // Mark that initial mount is complete after setting up the first load
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filters.source, filters.status, filters.score, filters.owner, filters.assignee, filters.region, filters.companySource, filters.startDate, filters.endDate, filters.leadVideoStatus, filters.leadOrderStatus, filters.leadCallStatus, filters.leadInterest, filters.duplicateFilter, currentPage, itemsPerPage, loadLeads, isSalesScopedUser, currentUserId]);

  // Map backend source to frontend display
  const mapSourceToFrontend = (source) => {
    if (!source) return 'Manual Entry';

    // 1. Handle object
    if (typeof source === 'object') {
      return source.name || source.label || source.id || 'Manual Entry';
    }

    // 2. Normalize string/ID
    const sourceStr = String(source).trim();

    // 3. Check master sources
    if (Array.isArray(masterSources) && masterSources.length > 0) {
      const masterSource = masterSources.find(s =>
        String(s._id) === sourceStr ||
        String(s.id) === sourceStr ||
        (s.leadSourceCode && s.leadSourceCode.toLowerCase() === sourceStr.toLowerCase()) ||
        (s.name && s.name.toLowerCase() === sourceStr.toLowerCase())
      );
      if (masterSource) return masterSource.name;
    }

    const sourceMap = {
      'website': 'Website',
      'facebook': 'Facebook',
      'instagram': 'Instagram',
      'linkedin': 'LinkedIn',
      'google_ads': 'Google Ads',
      'whatsapp': 'WhatsApp',
      'referral': 'Referral',
      'cold_call': 'Cold Call',
      'walk_in': 'Walk In',
      'manual_entry': 'Manual Entry',
      'other': 'Other'
    };

    const normalizedKey = sourceStr.toLowerCase();
    return sourceMap[normalizedKey] || sourceStr || 'Manual Entry';
  };

  // Map frontend source to backend source
  const mapSourceToBackend = (source) => {
    // Check master sources first via ref to avoid stale closure
    const currentMasterSources = masterSourcesRef.current || [];
    const sourceLower = source ? String(source).toLowerCase() : '';
    const masterSource = currentMasterSources.find(
      s => (s.name && s.name.toLowerCase() === sourceLower) || s._id === source || s.id === source || (s.leadSourceCode && s.leadSourceCode.toLowerCase() === sourceLower)
    );
    if (masterSource) return masterSource.leadSourceCode || masterSource._id;

    const sourceMap = {
      'website': 'website',
      'facebook': 'facebook',
      'instagram': 'instagram',
      'linkedin': 'linkedin',
      'google ads': 'google_ads',
      'whatsapp': 'whatsapp',
      'referral': 'referral',
      'cold call': 'cold_call',
      'walk in': 'walk_in',
      'manual entry': 'manual_entry',
      'other': 'other'
    };
    return sourceMap[sourceLower] || source?.toLowerCase() || 'other';
  };

  // Map frontend status to backend stage
  const mapStatusToBackend = (status) => {
    return status;
  };

  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      search: searchTerm?.trim() || undefined,
      source: filters.source !== 'all' ? mapSourceToBackend(filters.source) : undefined,
      stage: filters.status !== 'all' ? mapStatusToBackend(filters.status) : undefined,
      scoreLabel: filters.score !== 'all' ? filters.score.toLowerCase() : undefined,
      assignedTo: isSalesExecutive
        ? (currentUserId || undefined)
        : (filters.assignee !== 'all'
          ? (filters.assignee === 'Unassigned' ? 'unassigned' : filters.assignee)
          : (isSalesManager ? undefined : getAssignedToFilter())),
      region: filters.region !== 'all' ? filters.region : undefined,
      companySource: filters.companySource !== 'all' ? filters.companySource : undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      leadVideoStatus: filters.leadVideoStatus !== 'all' ? filters.leadVideoStatus : undefined,
      leadOrderStatus: filters.leadOrderStatus !== 'all' ? filters.leadOrderStatus : undefined,
      leadCallStatus: filters.leadCallStatus !== 'all' ? filters.leadCallStatus : undefined,
      leadInterest: filters.leadInterest !== 'all' ? filters.leadInterest : undefined,
      duplicateFilter: filters.duplicateFilter !== 'all' ? filters.duplicateFilter : undefined,
    };
    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) {
        delete apiFilters[key];
      }
    });
    return apiFilters;
  }, [
    searchTerm,
    filters.source,
    filters.status,
    filters.score,
    filters.owner,
    filters.assignee,
    filters.region,
    filters.companySource,
    filters.startDate,
    filters.endDate,
    filters.leadVideoStatus,
    filters.leadOrderStatus,
    filters.leadCallStatus,
    filters.leadInterest,
    filters.duplicateFilter,
    isSalesExecutive,
    isSalesManager,
    currentUserId,
    masterSources,
  ]);

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageLeadIds)
          : [];
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'crm-leads',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: sortColumn,
            sortOrder: sortDirection,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `crm-leads-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      sortColumn,
      sortDirection,
      currentPageLeadIds,
    ]
  );

  const handleCrmLeadsSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected leads to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} lead(s) exported.`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleCrmLeadsScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        const totalFiltered = Number(pagination?.totalLeads ?? 0);

        if (scope === 'currentPage' && !filteredLeads.length) {
          toast.error('No data on current page to export.');
          return;
        }

        if (scope === 'filtered' && totalFiltered === 0) {
          toast.error('No leads match the current filters.');
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: itemsPerPage,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageLeadIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      filteredLeads.length,
      pagination?.totalLeads,
      baseExportFilters,
      requestBackendExport,
      itemsPerPage,
      currentPageLeadIds,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const getScoreColor = (score) => {
    switch (score) {
      case 'Hot': return 'bg-red-100 text-red-800';
      case 'Warm': return 'bg-yellow-100 text-yellow-800';
      case 'Cold': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    return getLeadStatusColor(status);
  };
  const getStageDisplayName = (stage) => {
    if (!stage) return 'New';
    const stageValue = typeof stage === 'string' ? stage : (stage.value || '');
    const found = masterLeadStatuses.find((status) => status.value === stageValue);
    if (found?.name) return found.name;
    return mapStageToStatusName(stageValue) || stageValue.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const handleViewLead = (leadId) => {
    if (onViewLead) {
      onViewLead(leadId);
    } else {
      navigate(`/lead_detail_360/${leadId}`);
    }
  };

  const getVideoVerificationStatus = (lead) => {
    if (!lead.companyDocuments || !Array.isArray(lead.companyDocuments)) return null;
    const videoDocs = lead.companyDocuments.filter(doc => doc.fileType === 'video' || !!doc.videoUrl);
    if (videoDocs.length === 0) return null;
    if (videoDocs.some(doc => doc.verificationStatus === 'approved')) {
      return 'approved';
    }
    if (videoDocs.some(doc => doc.verificationStatus === 'rejected')) {
      return 'rejected';
    }
    return null;
  };

  const handleViewLeadInDocuments = (leadId) => {
    navigate(`/lead_detail_360/${leadId}?tab=documents`);
  };

  const handleEditLead = (leadId) => {
    // Navigate to manual lead entry page for editing
    if (onEditLead) {
      onEditLead(leadId);
    } else {
      navigate(`/manual_lead_entry?edit=${leadId}`);
    }
  };

  const handleAssignLead = async (leadId, assigneeId) => {
    // If no assigneeId provided, perform default navigation behavior
    if (!assigneeId) {
      if (onAssignLead) {
        onAssignLead(leadId);
      } else {
        navigate('/lead_assignment');
      }
      return;
    }

    // Manual assignment logic
    try {
      const result = await assignmentRuleService.manuallyAssignLead(leadId, assigneeId, 'Manually assigned');
      if (result.success) {
        toast.success('Lead assigned successfully!');
        // Reload leads to reflect changes
        loadLeads(buildLeadApiFilters());
      } else {
        toast.error(result.error || 'Failed to assign lead');
      }
    } catch (error) {
      toast.error('Failed to assign lead');
    }
  };

  const handleAddLead = () => {
    if (onAddLead) {
      onAddLead();
    } else {
      navigate('/manual_lead_entry');
    }
  };

  // Load CRM users for assignee selection
  const loadUsers = React.useCallback(async (search = '', forLeadId = null) => {
    setLoadingUsers(true);
    try {
      const trimmedSearch = (search || '').trim();
      const params = new URLSearchParams();
      if (trimmedSearch) {
        params.append('search', trimmedSearch);
      }
      params.append('limit', forLeadId ? '20' : '50');
      params.append('roleFilter', 'sales');

      // If user is a sales manager or export manager, filter subordinates only
      if (user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER) {
        params.append('managerId', user._id || user.id);
      }

      const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}${params.toString() ? `?${params.toString()}` : ''}`;

      const response = await authFetch(url, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      let exportManagers = [];
      if (user?.role === USER_ROLES.SALES_MANAGER) {
        try {
          const exportManagerParams = new URLSearchParams();
          if (trimmedSearch) {
            exportManagerParams.append('search', trimmedSearch);
          }
          exportManagerParams.append('limit', 'all');
          exportManagerParams.append('roleFilter', 'sales');
          const exportManagerUrl = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}?${exportManagerParams.toString()}`;
          const emResponse = await authFetch(exportManagerUrl, {
        credentials: 'include',
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          const emResult = await emResponse.json();
          if (emResult.responseCode === 200 && emResult.data?.users) {
            exportManagers = emResult.data.users.filter(u => u.role === USER_ROLES.EXPORT_MANAGER || u.designation === 'Export Manager');
          }
        } catch (e) {
        }
      }

      if (result.responseCode === 200) {
        const crmUsers = result.data?.users || [];
        const allUsers = [...crmUsers, ...exportManagers];

        // Deduplicate merged array by checking the mapped user ID
        const uniqueUsersMap = new Map();
        allUsers.forEach(u => {
          const id = u.user || u._id || u.id;
          if (id) {
            uniqueUsersMap.set(id, u);
          }
        });
        const mergedUsers = Array.from(uniqueUsersMap.values());

        let transformedUsers = mergedUsers.map(user => ({
          id: user.user || user._id || user.id,
          name: user.name || 'Unknown',
          email: user.email || '',
          department: user.department || '',
          role: user.role || '',
          designation: user.designation || ''
        })).sort((a, b) => a.name.localeCompare(b.name));

        // If the logged-in user is a sales manager or export manager, add them to the list of assignees
        if (user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER) {
          const managerUser = {
            id: currentUserId,
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Me',
            email: user?.email || '',
            department: user?.department || '',
            role: user?.role || '',
            designation: user?.designation || ''
          };
          const alreadyExists = transformedUsers.some(u => u.id === currentUserId);
          const matchesSearch = !trimmedSearch ||
            managerUser.name.toLowerCase().includes(trimmedSearch.toLowerCase()) ||
            managerUser.email.toLowerCase().includes(trimmedSearch.toLowerCase());

          if (!alreadyExists && matchesSearch) {
            transformedUsers = [managerUser, ...transformedUsers];
          }
        }

        if (forLeadId === 'bulk') {
          setBulkAssignUsers(transformedUsers);
          return transformedUsers;
        }

        if (forLeadId) {
          setLeadAllUsers(prev => ({ ...prev, [forLeadId]: transformedUsers }));
          setLeadFilteredUsers(prev => ({ ...prev, [forLeadId]: transformedUsers.slice(0, 10) }));
          return transformedUsers;
        }
        return [];
      }

      if (forLeadId) {
        setLeadFilteredUsers(prev => ({ ...prev, [forLeadId]: [] }));
        setLeadAllUsers(prev => ({ ...prev, [forLeadId]: [] }));
      }
      return [];
    } catch (error) {
      if (forLeadId) {
        setLeadFilteredUsers(prev => ({ ...prev, [forLeadId]: [] }));
        setLeadAllUsers(prev => ({ ...prev, [forLeadId]: [] }));
      }
      return [];
    } finally {
      setLoadingUsers(false);
    }

  }, []);

  // Simplified loadUsers for AssignmentRuleModal (similar to LeadAssignment logic)
  const loadUsersForRuleModal = React.useCallback(async (search = '') => {
    setLoadingUsers(true);
    try {
      const trimmedSearch = (search || '').trim();
      const params = new URLSearchParams();
      if (trimmedSearch) {
        params.append('search', trimmedSearch);
      }
      params.append('limit', '50');
      params.append('roleFilter', 'sales');

      // If user is a sales manager or export manager, filter subordinates only
      if (user?.role === USER_ROLES.SALES_MANAGER || user?.role === USER_ROLES.EXPORT_MANAGER) {
        params.append('managerId', user._id || user.id);
      }

      const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();

      if (result.responseCode === 200) {
        const crmUsers = result.data?.users || [];
        const transformedUsers = crmUsers.map(user => ({
          id: user.user || user._id || user.id,
          name: user.name || 'Unknown',
          email: user.email || '',
          department: user.department || '',
          role: user.role || '',
          designation: user.designation || ''
        })).sort((a, b) => a.name.localeCompare(b.name));

        setUsers(prev => {
          const existingMap = new Map(prev.map(u => [u.id, u]));
          transformedUsers.forEach(u => existingMap.set(u.id, u));
          return Array.from(existingMap.values());
        });
        setFilteredUsers(transformedUsers);
        return transformedUsers;
      }
      setFilteredUsers([]);
      return [];
    } catch (error) {
      setFilteredUsers([]);
      return [];
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadUsersForLead = React.useCallback(async (leadId, searchTerm = '') => {
    await loadUsers(searchTerm, leadId);
  }, [loadUsers]);

  // Debounced search for per-lead assignee dropdowns
  useEffect(() => {
    if (!openDropdownLeadId) return;
    const searchTerm = leadSearchTerms[openDropdownLeadId] || '';
    const handler = setTimeout(() => {
      loadUsersForLead(openDropdownLeadId, searchTerm);
    }, 400);
    return () => clearTimeout(handler);
  }, [openDropdownLeadId, leadSearchTerms, loadUsersForLead]);

  // Calculate dropdown position
  useEffect(() => {
    if (openDropdownLeadId) {
      const ref = leadDropdownRefs.current[openDropdownLeadId];
      if (ref) {
        const rect = ref.getBoundingClientRect();
        setDropdownPositions(prev => ({
          ...prev,
          [openDropdownLeadId]: {
            top: rect.bottom + window.scrollY + 8,
            left: rect.left + window.scrollX,
            width: rect.width
          }
        }));
      }
    }
  }, [openDropdownLeadId]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(leadDropdownRefs.current).forEach(leadId => {
        const ref = leadDropdownRefs.current[leadId];
        if (ref && !ref.contains(event.target)) {
          const dropdownElement = document.querySelector(`[data-dropdown-lead-id="${leadId}"]`);
          if ((dropdownElement && !dropdownElement.contains(event.target)) || (!dropdownElement && openDropdownLeadId === leadId)) {
            if (openDropdownLeadId === leadId) {
              setOpenDropdownLeadId(null);
              setLeadSearchTerms(prev => ({ ...prev, [leadId]: '' }));
            }
          }
        }
      });
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownLeadId]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredLeads.map(lead => lead.id || lead._id || lead.leadId);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleBulkAssignClick = () => {
    if (selectedRows.size > 0) {
      setShowBulkAssignModal(true);
      // Load users for the bulk assign dropdown
      loadUsers('', 'bulk');
    }
  };

  const handleConfirmBulkAssign = async () => {
    if (!bulkAssignee) {
      toast.error('Please select a user to assign leads to');
      return;
    }

    setIsAssigningBulk(true);
    try {
      const leadIds = Array.from(selectedRows);
      const result = await bulkAssignLeads(leadIds, bulkAssignee);

      if (result.success) {
        toast.success(result.message || `Successfully assigned ${leadIds.length} leads`);
        setSelectedRows(new Set());
        setShowBulkAssignModal(false);
        setBulkAssignee(null);
        // Refresh leads list
        await loadLeads(buildLeadApiFilters());
      } else {
        toast.error(result.error || 'Failed to assign leads');
      }
    } catch (err) {
      toast.error(err.message || 'An error occurred during bulk assignment');
    } finally {
      setIsAssigningBulk(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedRows.size > 0) {
      setShowBulkDeleteModal(true);
    }
  };

  const handleConfirmBulkDelete = async () => {
    setIsDeletingBulk(true);
    try {
      const leadIds = Array.from(selectedRows);
      const result = await bulkDeleteLeads(leadIds);

      if (result.success) {
        toast.success(result.message || `Successfully deleted ${leadIds.length} leads`);
        setSelectedRows(new Set());
        setShowBulkDeleteModal(false);
        // Refresh leads list
        await loadLeads(buildLeadApiFilters());
      } else {
        toast.error(result.error || 'Failed to delete leads');
      }
    } catch (err) {
      toast.error(err.message || 'An error occurred during bulk deletion');
    } finally {
      setIsDeletingBulk(false);
    }
  };

  // --- Conversion Handlers ---

  const handleConvertLead = (lead) => {
    setSelectedLeadForConversion(lead);
    setShowConvertModal(true);
  };

  const handleConvertToOpportunity = async (lead, opportunityData) => {
    try {
      // Get lead ID (could be _id or leadId)
      const leadId = lead._id || lead.id || lead.leadId;

      if (!leadId) {
        throw new Error('Lead ID not found');
      }

      // Call API to convert lead to opportunity
      const result = await leadService.convertToOpportunity(leadId, opportunityData);

      if (result.success) {
        const leadName = getLeadName(lead);
        toast.success(`Lead "${leadName}" converted to opportunity successfully!`);

        // Close modal
        setSelectedLeadForConversion(null);
        setShowConvertModal(false);

        // Refresh leads list
        await loadLeads(buildLeadApiFilters());
      } else {
        throw new Error(result.error || 'Failed to convert lead to opportunity');
      }
    } catch (error) {
      let errorMessage = 'Failed to convert lead to opportunity';
      if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      throw error; // Re-throw to let the modal know it failed
    }
  };

  // Show delete confirmation modal - DO NOT call API here
  const handleDeleteClick = (leadId) => {
    setLeadToDelete(leadId);
    setShowDeleteModal(true);
  };

  // Close delete modal without deleting
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setLeadToDelete(null);
  };

  // Confirm delete - ONLY call API when user clicks Confirm button
  const handleConfirmDelete = async () => {
    if (!leadToDelete) {
      setShowDeleteModal(false);
      return;
    }

    try {
      const result = await deleteLead(leadToDelete);
      if (result.success) {
        toast.success('Lead deleted successfully');
        // Refresh the leads list with current pagination
        await loadLeads(buildLeadApiFilters());
      } else {
        toast(result.error || 'Unable to delete lead. Please try again.', {
          icon: '⚠️',
          style: { background: '#f59e0b', color: '#fff' }
        });
      }
    } catch (error) {
      toast('Unable to delete lead. Please check your connection and try again.', {
        icon: '⚠️',
        style: { background: '#f59e0b', color: '#fff' }
      });
    } finally {
      // Close modal after API call completes
      setShowDeleteModal(false);
      setLeadToDelete(null);
    }
  };

  // Show confirmation modal for auto qualification
  const handleShowAutoQualificationModal = () => {
    setShowAutoQualificationModal(true);
  };

  // Run auto qualification for all unqualified leads (called after confirmation)
  const handleRunAutoQualification = async () => {
    setShowAutoQualificationModal(false);
    setIsRunningAutoQualification(true);
    setAutoQualificationProgress({ processed: 0, total: 0, qualified: 0 });

    try {
      // First, get all unqualified leads (we'll fetch in batches)
      let allUnqualifiedLeads = [];
      let currentPage = 1;
      let hasMore = true;
      const batchSize = 100; // Process 100 leads at a time

      // Fetch all unqualified leads
      while (hasMore) {
        const result = await leadScoringService.getLeadScores({
          page: currentPage,
          limit: batchSize,
          isQualified: false
        });

        if (result.success && result.leads && result.leads.length > 0) {
          allUnqualifiedLeads = [...allUnqualifiedLeads, ...result.leads];
          currentPage++;
          hasMore = result.pagination?.hasNextPage || false;
        } else {
          hasMore = false;
        }
      }

      const totalLeads = allUnqualifiedLeads.length;
      setAutoQualificationProgress({ processed: 0, total: totalLeads, qualified: 0 });

      if (totalLeads === 0) {
        toast('No disqualified leads found to process', {
          icon: 'ℹ️',
          style: { background: '#3b82f6', color: '#fff' }
        });
        setIsRunningAutoQualification(false);
        return;
      }

      let qualifiedCount = 0;
      let processedCount = 0;

      // Process leads in smaller batches to avoid overwhelming the server
      const processBatchSize = 10;
      for (let i = 0; i < allUnqualifiedLeads.length; i += processBatchSize) {
        const batch = allUnqualifiedLeads.slice(i, i + processBatchSize);

        // Process batch in parallel - only qualify, don't recalculate scores
        const batchResults = await Promise.all(
          batch.map(async (lead) => {
            try {
              const leadId = lead._id || lead.id || lead.leadId;
              const result = await leadScoringService.qualifyLeadOnly(leadId);

              return {
                success: result.success,
                isQualified: result.data?.isQualified || false
              };
            } catch (error) {
              return {
                success: false,
                isQualified: false
              };
            }
          })
        );

        // Update counts after batch completes
        batchResults.forEach((result) => {
          processedCount++;
          if (result.success && result.isQualified) {
            qualifiedCount++;
          }
        });

        // Update progress
        setAutoQualificationProgress({
          processed: processedCount,
          total: totalLeads,
          qualified: qualifiedCount
        });

        // Small delay between batches to avoid overwhelming the server
        if (i + processBatchSize < allUnqualifiedLeads.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Reload leads to show updated results
      const apiFilters = buildLeadApiFilters();
      await loadLeads(apiFilters);

      toast.success(
        `Processed ${processedCount} leads, ${qualifiedCount} newly qualified.`,
        { duration: 6000 }
      );
    } catch (error) {
      toast('Failed to run auto-qualification. Please try again.', {
        icon: '⚠️',
        style: { background: '#f59e0b', color: '#fff' }
      });
    } finally {
      setIsRunningAutoQualification(false);
      setAutoQualificationProgress({ processed: 0, total: 0, qualified: 0 });
    }
  };

  // Show confirmation modal for auto assignment
  const handleShowAutoAssignmentModal = () => {
    setShowAutoAssignmentModal(true);
  };

  // Run auto assignment
  const handleRunAutoAssignment = async () => {
    setShowAutoAssignmentModal(false);
    setIsRunningAutoAssignment(true);
    try {
      const result = await assignmentRuleService.runAutoAssignment();
      if (result.success) {
        toast.success(
          `Auto-assignment completed. Processed ${result.results.processedCount || 0} leads, assigned ${result.results.assignedCount || 0}.`,
          { duration: 5000 }
        );
        // Refresh leads
        await loadLeads(buildLeadApiFilters());
      } else {
        toast.error(result.error || 'Failed to run auto-assignment');
      }
    } catch (error) {
      toast.error('Failed to run auto-assignment');
    } finally {
      setIsRunningAutoAssignment(false);
    }
  };

  // Check if any filters or search are active
  const hasActiveFilters = useMemo(() => {
    return (
      searchTerm.trim() !== '' ||
      filters.source !== 'all' ||
      filters.status !== 'all' ||
      filters.score !== 'all' ||
      filters.owner !== 'all' ||
      filters.assignee !== 'all' ||
      filters.region !== 'all' ||
      filters.companySource !== 'all' ||
      filters.startDate !== '' ||
      filters.endDate !== '' ||
      filters.leadVideoStatus !== 'all' ||
      filters.leadOrderStatus !== 'all' ||
      filters.leadCallStatus !== 'all' ||
      filters.leadInterest !== 'all' ||
      filters.duplicateFilter !== 'all'
    );
  }, [searchTerm, filters]);

  // Clear all filters and search - let useEffect handle the API call (same as LeadActivities)
  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({
      source: 'all',
      status: 'all',
      score: 'all',
      owner: 'all',
      assignee: 'all',
      region: 'all',
      companySource: 'all',
      startDate: '',
      endDate: '',
      leadVideoStatus: 'all',
      leadOrderStatus: 'all',
      leadCallStatus: 'all',
      leadInterest: 'all',
      duplicateFilter: 'all'
    });
    setCurrentPage(1);
    setAssigneeSearchTerm('');
    setOpenAssigneeDropdown(false);
    // Don't call loadLeads directly - let the useEffect handle it to prevent duplicate calls
  };

  // Debug logging
  useEffect(() => {
  }, [leads?.length, loading, error, filteredLeads]);

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="p-3 bg-red-100 rounded-full mb-4 mx-auto w-fit">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Leads</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button
            variant="primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Lead List & Filters</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage and filter your leads with advanced search capabilities</p>
        </div>
        <div className="flex items-center space-x-3">
          <PermissionGate permission="lead_management" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleAddLead}
              className="flex-shrink-0 bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              Add Lead
            </Button>
          </PermissionGate>
          {canModify && (
            <>
              <Button
                variant="primary"
                icon={isRunningAutoQualification ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Target className="h-4 w-4" />}
                onClick={handleShowAutoQualificationModal}
                disabled={isRunningAutoQualification}
                className="flex-shrink-0 bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              >
                {isRunningAutoQualification
                  ? `Processing... (${autoQualificationProgress.processed}/${autoQualificationProgress.total})`
                  : 'Run Auto Qualification'
                }
              </Button>
              <Button
                variant="primary"
                icon={isRunningAutoAssignment ? <RefreshCw className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                onClick={handleShowAutoAssignmentModal}
                disabled={isRunningAutoAssignment}
                className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              >
                {isRunningAutoAssignment ? 'Running...' : 'Run Auto Assignment'}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-3 flex-wrap gap-y-2">
        {!isSalesExecutive && (
          <div className="relative">
            <button
              ref={assigneeDropdownRef}
              onClick={() => {
                const isOpening = !openAssigneeDropdown;
                setOpenAssigneeDropdown(isOpening);
                if (isOpening) {
                  setFilteredAssigneeOptions(assigneeOptions);
                }
              }}
              disabled={loadingAssignees}
              className="px-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <span className="truncate text-sm whitespace-nowrap">
                {assigneeOptions.find(opt => opt.id === filters.assignee)?.name || 'All'}
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${openAssigneeDropdown ? 'rotate-180' : ''}`} />
            </button>

            {/* Assignee Dropdown */}
            {openAssigneeDropdown && (
              <div
                data-dropdown-assignee-filter
                className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                style={{
                  top: `${assigneeDropdownPosition.top}px`,
                  left: `${assigneeDropdownPosition.left}px`,
                  minWidth: '240px',
                  maxWidth: '350px',
                  width: `${Math.max(240, assigneeDropdownPosition.width)}px`
                }}
              >
                <div className="p-2 border-b border-gray-100">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={assigneeSearchTerm}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                        setAssigneeSearchTerm(value);
                      }}
                      placeholder="Search assignees..."
                      maxLength={100}
                      className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                  {loadingAssignees && filteredAssigneeOptions.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500 text-center">
                      Loading assignees...
                    </div>
                  ) : filteredAssigneeOptions.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500 text-center">
                      {assigneeSearchTerm ? 'No assignees found' : 'No assignees available'}
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      {filteredAssigneeOptions.map(option => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            setFilters({ ...filters, assignee: option.id });
                            setOpenAssigneeDropdown(false);
                            setAssigneeSearchTerm('');
                          }}
                          className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 flex flex-col ${filters.assignee === option.id
                            ? 'bg-agrosaf-green/10 text-agrosaf-green font-medium'
                            : 'hover:bg-gray-50 text-gray-700'
                            }`}
                        >
                          <div className="truncate font-medium break-words">{option.name}</div>
                          {option.email && (
                            <div className="text-xs text-gray-500 truncate break-words">{option.email}</div>
                          )}
                          {option.role && (
                            <div className="text-xs text-gray-500 truncate capitalize break-words">{option.role}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        <PermissionGate permission="lead_management" action="read">
          <div className="relative shrink-0" ref={selectedExportWrapRef}>
            <Button
              variant="outline"
              icon={
                exportLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                ) : (
                  <Download className="h-4 w-4" />
                )
              }
              onClick={handleExportClick}
              disabled={exportLoading}
              className={`flex-shrink-0 ${selectedRows.size > 0 ? 'bg-blue-50 border-blue-200 text-blue-600' : ''}`}
              aria-label={
                selectedRows.size > 0
                  ? `Export ${selectedRows.size} selected leads`
                  : 'Export leads'
              }
            >
              {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
            </Button>
            <SelectedExportFormatBar
              show={selectedRows.size > 0 && showInlineSelectedFormats}
              panelEntered={inlineFormatPanelEntered}
              disabled={exportLoading}
              onSelect={handleCrmLeadsSelectedFormatExport}
            />
          </div>
        </PermissionGate>
        {selectedRows.size > 0 && (
          <PermissionGate permission="lead_management" action="update">
            <Button
              variant="outline"
              icon={<UserPlus className="h-4 w-4" />}
              onClick={handleBulkAssignClick}
              className="flex-shrink-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
            >
              Assign ({selectedRows.size})
            </Button>
          </PermissionGate>
        )}
        {selectedRows.size > 0 && user?.role === 'super_admin' && (
          <PermissionGate permission="lead_management" action="delete">
            <Button
              variant="outline"
              icon={<Trash2 className="h-4 w-4" />}
              onClick={handleBulkDelete}
              className="flex-shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            >
              Delete ({selectedRows.size})
            </Button>
          </PermissionGate>
        )}
        <PermissionGate permission="lead_management" action="create">
          <Button
            variant="outline"
            icon={<Upload className="h-4 w-4" />}
            onClick={() => setShowBulkUploadModal(true)}
            className="flex-shrink-0"
          >
            Bulk Upload
          </Button>
        </PermissionGate>
        {canModify && (
          <>
            <Button
              variant="outline"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setShowRuleModal(true);
                loadUsersForRuleModal(''); // Initial load of users
              }}
              className="flex-shrink-0 text-agrosaf-green border-agrosaf-green hover:bg-green-50"
            >
              Assignment Rule
            </Button>
            <Button
              variant="outline"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setSelectedScoring(null); // Create mode
                setScoringFormData({
                  name: '',
                  description: '',
                  rules: [],
                  isActive: true
                });
                setScoringErrors({});
                setShowScoringModal(true);
              }}
              className="flex-shrink-0 text-agrosaf-green border-agrosaf-green hover:bg-green-50"
            >
              Scoring Rule
            </Button>
            <Button
              variant="outline"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => {
                setSelectedCriteria(null);
                setCriteriaFormData({
                  name: '',
                  description: '',
                  criteria: [],
                  isActive: true,
                  qualificationLogic: 'all',
                  qualificationResult: 'qualified',
                  leadTemperature: 'Cold'
                });
                setCriteriaErrors({});
                setShowCriteriaModal(true);
              }}
              className="flex-shrink-0 text-agrosaf-green border-agrosaf-green hover:bg-green-50"
            >
              Qualification Criteria
            </Button>
          </>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[300px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by Lead ID, name, email, company, or owner..."
              value={searchTerm}
              onChange={(e) => {
                // Direct state update without double setState - same as LeadActivities
                const sanitized = handleSearchInputChange(e, null, FIELD_LIMITS.search);
                setSearchTerm(sanitized);
              }}
              onKeyDown={(e) => {
                // Prevent form submission on Enter key
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
              autoComplete="off"
              maxLength={FIELD_LIMITS.search}
              autoFocus
            />
          </div>
          <select
            value={filters.source}
            onChange={(e) => setFilters({ ...filters, source: e.target.value })}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
          >
            <option value="all">All Sources</option>
            {masterSources.map(source => (
              <option key={source._id} value={source.name} className="capitalize">{source.name}</option>
            ))}

          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
          >
            <option value="all">All Status</option>
            {masterLeadStatuses.map(status => (
              <option key={status._id} value={status.value}>{status.name}</option>
            ))}
          </select>
          <select
            value={filters.score}
            onChange={(e) => setFilters({ ...filters, score: e.target.value })}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
          >
            <option value="all">All Temp</option>
            <option value="Hot">Hot</option>
            <option value="Warm">Warm</option>
            <option value="Cold">Cold</option>
          </select>
          <select
            value={filters.companySource}
            onChange={(e) => setFilters({ ...filters, companySource: e.target.value })}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
          >
            <option value="all">All Co. Sources</option>
            {companySources.map(source => (
              <option key={source.id} value={source.id}>{source.name}</option>
            ))}
          </select>
          <select
            value={filters.duplicateFilter}
            onChange={(e) => setFilters({ ...filters, duplicateFilter: e.target.value })}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent font-medium"
          >
            <option value="all">All Leads</option>
            <option value="duplicates">Duplicates Only</option>
          </select>
          <select
            value={filters.leadVideoStatus}
            onChange={(e) => setFilters({ ...filters, leadVideoStatus: e.target.value })}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
          >
            <option value="all">All Video Statuses</option>
            {masterVideoStatuses.map(status => (
              <option key={status._id} value={status._id} className='capitalize'>{status.name}</option>
            ))}
          </select>
          <select
            value={filters.leadOrderStatus}
            onChange={(e) => setFilters({ ...filters, leadOrderStatus: e.target.value })}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
          >
            <option value="all">All Order Statuses</option>
            {masterOrderStatuses.map(status => (
              <option key={status._id} value={status._id} className='capitalize'>{status.name}</option>
            ))}
          </select>
          <select
            value={filters.leadCallStatus}
            onChange={(e) => setFilters({ ...filters, leadCallStatus: e.target.value })}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
          >
            <option value="all">All Call Statuses</option>
            {masterCallStatuses.map(status => (
              <option key={status._id} value={status._id} className='capitalize'>{status.name}</option>
            ))}
          </select>
          <select
            value={filters.leadInterest}
            onChange={(e) => setFilters({ ...filters, leadInterest: e.target.value })}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
          >
            <option value="all">All Interests</option>
            {masterInterests.map(interest => (
              <option key={interest._id} value={interest._id} className='capitalize'>{interest.name}</option>
            ))}
          </select>

          <DateRangeFilter
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={({ startDate, endDate }) => setFilters({ ...filters, startDate, endDate })}
          />
          {hasActiveFilters && (
            <Button
              variant="outline"
              icon={<X className="h-4 w-4" />}
              onClick={handleClearFilters}
              className="flex-shrink-0 whitespace-nowrap"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Loading State - Only show in table area, not full page */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading leads...</p>
            </div>
          </div>
        )}

        {/* Desktop Table View */}
        {!loading && (
          <div className="hidden lg:block overflow-x-auto w-full">
            <table className="min-w-[1200px] divide-y divide-gray-200 table-auto w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={filteredLeads.length > 0 && selectedRows.size === filteredLeads.length}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded cursor-pointer"
                    />
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('leadId')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Lead ID</span>
                      {sortColumn === 'leadId' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[220px] max-w-[220px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('leadDetails')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Lead Details</span>
                      {sortColumn === 'leadDetails' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('source')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Source</span>
                      {sortColumn === 'source' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[110px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('companySource')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Company Source</span>
                      {sortColumn === 'companySource' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>

                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortColumn === 'status' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('scoreLabel')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Temperature</span>
                      {sortColumn === 'scoreLabel' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">
                    Video Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">
                    Order Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">
                    Call Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">
                    Interest
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('owner')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Owner</span>
                      {sortColumn === 'owner' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('value')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Value</span>
                      {sortColumn === 'value' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('lastActivity')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Last Activity</span>
                      {sortColumn === 'lastActivity' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created At</span>
                      {sortColumn === 'createdAt' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('updatedAt')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Last Updated</span>
                      {sortColumn === 'updatedAt' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  {!isSalesExecutive && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                      Assignee
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan="19" className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-lg font-medium">No leads found</p>
                        <p className="text-sm mt-2">
                          {leads.length === 0
                            ? 'No leads have been created yet. Create your first lead to get started.'
                            : 'Try adjusting your filters to see more results.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredLeads.map((lead) => {
                    const leadId = lead.id || lead._id || lead.leadId;
                    return (
                      <tr key={leadId} className={`hover:bg-gray-50 ${selectedRows.has(leadId) ? 'bg-green-50' : ''}`}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(leadId)}
                            onChange={() => handleSelectRow(leadId)}
                            className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[100px]">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewLead(leadId)}
                              className="text-xs text-center text-agrosaf-green hover:text-green-700 hover:underline font-bold font-mono transition-colors"
                              title={`View details for ${lead.leadId || 'lead'}`}
                            >
                              {lead.leadId || 'No ID'}
                            </button>
                            {lead.duplicateInfo?.isDuplicate && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setDuplicateModalState({
                                    isOpen: true,
                                    matches: lead.duplicateInfo.matches,
                                    sourceLeadId: lead.leadId || leadId,
                                    position: { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX }
                                  });
                                }}
                                className="w-5 h-5 flex items-center justify-center rounded bg-red-100 text-red-600 text-[10px] font-bold border border-red-200 hover:bg-red-200 transition-colors cursor-pointer shrink-0"
                                title="Duplicate matches found"
                              >
                                D
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 min-w-[200px] max-w-[200px]">
                          <div className="flex items-center space-x-3 min-w-0">
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <button
                                onClick={() => handleViewLead(leadId)}
                                className="text-sm font-medium text-gray-900 truncate capitalize hover:text-agrosaf-green hover:underline text-left block w-full transition-colors"
                                title={getLeadName(lead)}
                              >
                                {getLeadName(lead)}
                              </button>

                              <div className="text-sm text-gray-500 truncate first-letter:capitalize" title={lead.company || 'No company'}>{lead.company || 'No company'}</div>
                              <div className="text-xs text-gray-500 truncate" title={lead.email || 'No email'}>{lead.email || 'No email'}</div>
                              <div className="text-xs text-gray-500 truncate" title={lead.phone || lead.alternatePhone || 'No phone'}>{lead.phone || lead.alternatePhone || 'No phone'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[100px]">
                          <span className="text-sm text-gray-900 truncate block capitalize" title={mapSourceToFrontend(lead.source)}>{mapSourceToFrontend(lead.source)}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[110px]">
                          <span className="text-sm text-gray-900 truncate block capitalize" title={lead.companySource || 'agrosaf'}>{lead.companySource || 'agrosaf'}</span>
                        </td>


                        <td className="px-4 py-4 whitespace-nowrap min-w-[100px]">
                          <div className="flex flex-col space-y-1 items-start">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(getStageDisplayName(lead.stage))}`}>
                              {getStageDisplayName(lead.stage)}
                            </span>
                            {(() => {
                              const status = getVideoVerificationStatus(lead);
                              if (status === 'approved') {
                                return (
                                  <button
                                    onClick={() => handleViewLeadInDocuments(leadId)}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize bg-green-100 text-green-800 hover:bg-green-200 transition-colors mt-1.5"
                                  >
                                    Video approved
                                  </button>
                                );
                              }
                              if (status === 'rejected') {
                                return (
                                  <button
                                    onClick={() => handleViewLeadInDocuments(leadId)}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize bg-red-100 text-red-800 hover:bg-red-200 transition-colors mt-1.5"
                                  >
                                    Video rejected
                                  </button>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[120px]">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${lead.scoreLabel === 'hot' ? 'bg-red-100 text-red-800' :
                            lead.scoreLabel === 'warm' ? 'bg-orange-100 text-orange-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                            {lead.scoreLabel === 'hot' ? '🔥 Hot' :
                              lead.scoreLabel === 'warm' ? '☀️ Warm' :
                                '❄️ Cold'
                            }
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[140px]">
                          <span className="text-sm text-gray-900 truncate block capitalize" title={lead.leadVideoStatus?.name || '-'}>
                            {lead.leadVideoStatus?.name || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[140px]">
                          <span className="text-sm text-gray-900 truncate block capitalize" title={lead.leadOrderStatus?.name || '-'}>
                            {lead.leadOrderStatus?.name || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[140px]">
                          <span className="text-sm text-gray-900 truncate block capitalize" title={lead.leadCallStatus?.name || '-'}>
                            {lead.leadCallStatus?.name || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[140px]">
                          <span className="text-sm text-gray-900 truncate block capitalize" title={lead.leadInterest?.name || '-'}>
                            {lead.leadInterest?.name || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[200px] max-w-[220px]">
                          <div className="overflow-hidden">
                            <span className="text-sm text-gray-900 first-letter:capitalize truncate block" title={lead.owner || 'Unassigned'}>{lead.owner || 'Unassigned'}</span>
                            {lead.ownerEmail && (
                              <span className="text-xs text-gray-500 truncate block" title={lead.ownerEmail}>{lead.ownerEmail}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[100px]">
                          <span className="text-sm font-medium text-gray-900">₹{lead.leadValue ? formatIndianAmount(lead.leadValue) : '0'}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[140px]">
                          <span className="text-sm text-gray-500 font-medium">{calculateDaysSince(lead.lastActivityDate)}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[140px]">
                          <span className="text-sm text-gray-500">{formatDate(lead.createdAt)}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[140px]">
                          <span className="text-sm text-gray-500">{formatDate(lead.updatedAt || lead.createdAt)}</span>
                        </td>
                        {!isSalesExecutive && (
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium" style={{ position: 'relative', overflow: 'visible' }}>
                            <div className="relative w-full" ref={el => leadDropdownRefs.current[leadId] = el}>
                              <button
                                type="button"
                                onClick={() => {
                                  const isOpening = openDropdownLeadId !== leadId;
                                  setOpenDropdownLeadId(isOpening ? leadId : null);
                                  if (isOpening && !leadAllUsers[leadId]) {
                                    loadUsersForLead(leadId, leadSearchTerms[leadId] || '');
                                  }
                                }}
                                disabled={loadingUsers}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                              >
                                <span className="truncate text-sm">Select Assignee</span>
                                <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${openDropdownLeadId === leadId ? 'rotate-180' : ''}`} />
                              </button>

                              {/* Dropdown */}
                              {openDropdownLeadId === leadId && dropdownPositions[leadId] && (
                                <div
                                  data-dropdown-lead-id={leadId}
                                  className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                                  style={{
                                    top: `${dropdownPositions[leadId].top}px`,
                                    left: `${dropdownPositions[leadId].left}px`,
                                    minWidth: '200px',
                                    maxWidth: '300px',
                                    width: `${Math.max(200, dropdownPositions[leadId].width)}px`
                                  }}
                                >
                                  <div className="p-2 border-b border-gray-100">
                                    <div className="relative">
                                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                      <input
                                        type="text"
                                        value={leadSearchTerms[leadId] || ''}
                                        onChange={(e) => {
                                          const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                          setLeadSearchTerms(prev => ({
                                            ...prev,
                                            [leadId]: value
                                          }));
                                        }}
                                        placeholder="Search users..."
                                        maxLength={100}
                                        className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                        autoFocus
                                      />
                                    </div>
                                  </div>
                                  <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                                    {loadingUsers && (!leadFilteredUsers[leadId] || leadFilteredUsers[leadId].length === 0) ? (
                                      <div className="p-3 text-sm text-gray-500 text-center">
                                        Loading users...
                                      </div>
                                    ) : (!leadFilteredUsers[leadId] || leadFilteredUsers[leadId].length === 0) ? (
                                      <div className="p-3 text-sm text-gray-500 text-center">
                                        {leadSearchTerms[leadId] ? 'No users found' : 'No users available'}
                                      </div>
                                    ) : (
                                      <div className="flex flex-col">
                                        {leadFilteredUsers[leadId].map(user => (
                                          <button
                                            key={user.id}
                                            type="button"
                                            onClick={() => {
                                              handleAssignLead(leadId, user.id);
                                              setOpenDropdownLeadId(null);
                                              setLeadSearchTerms(prev => ({
                                                ...prev,
                                                [leadId]: ''
                                              }));
                                            }}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 text-gray-700 flex flex-col"
                                          >
                                            <div className="truncate font-medium break-words">{user.name}</div>
                                            {user.email && (
                                              <div className="text-xs text-gray-500 truncate break-words">{user.email}</div>
                                            )}
                                            {user.designation && (
                                              <div className="text-xs text-gray-500 truncate capitalize break-words">{user.designation}</div>
                                            )}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium min-w-[100px]">
                          <div className="flex items-center space-x-2">
                            <PermissionGate permission="lead_management" action="read">
                              <button
                                onClick={() => handleViewLead(leadId)}
                                className="text-blue-600 hover:text-blue-900 flex-shrink-0"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="lead_management" action="update">
                              <button
                                onClick={() => handleEditLead(leadId)}
                                className="text-green-600 hover:text-green-900 flex-shrink-0"
                                title="Edit Lead"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="lead_management" action="update">
                              <button
                                onClick={() => navigate(`/log_activity/${leadId}`)}
                                className="text-amber-600 hover:text-amber-900 flex-shrink-0"
                                title="Log Activity"
                              >
                                <FileText className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="lead_management" action="create">
                              <button
                                onClick={() => handleConvertLead(lead)}
                                disabled={lead.stage === 'converted' || lead.stage === 'closed_won'}
                                className={`flex-shrink-0 ${(lead.stage === 'converted' || lead.stage === 'closed_won')
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : 'text-indigo-600 hover:text-indigo-900'
                                  }`}
                                title={lead.stage === 'closed_won' ? 'Won leads cannot be converted' : lead.stage === 'converted' ? 'Already Converted' : 'Convert to Opportunity'}
                              >
                                <ArrowRight className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                            {user?.role === 'super_admin' && (
                              <PermissionGate permission="lead_management" action="delete">
                                <button
                                  onClick={() => lead.stage !== 'converted' && lead.stage !== 'closed_won' && handleDeleteClick(leadId)}
                                  disabled={lead.stage === 'converted' || lead.stage === 'closed_won'}
                                  className={`flex-shrink-0 ${(lead.stage === 'converted' || lead.stage === 'closed_won')
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-red-600 hover:text-red-900'
                                    }`}
                                  title={lead.stage === 'closed_won' ? 'Won leads cannot be deleted' : lead.stage === 'converted' ? 'Converted leads cannot be deleted' : 'Delete Lead'}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile Card View */}
        {!loading && (
          <div className="lg:hidden">
            {filteredLeads.length > 0 && (
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedRows.size === filteredLeads.length}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded cursor-pointer"
                />
                <span className="text-sm font-medium text-gray-700">Select All ({filteredLeads.length})</span>
              </div>
            )}
            {filteredLeads.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium text-gray-900 mb-2">No leads found</p>
                <p className="text-sm text-gray-500">
                  {leads.length === 0
                    ? 'No leads have been created yet.'
                    : 'Try adjusting your filters.'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredLeads.map((lead) => {
                  const leadId = lead.id || lead._id || lead.leadId;
                  return (
                    <div key={leadId} className={`p-4 hover:bg-gray-50 ${selectedRows.has(leadId) ? 'bg-green-50' : ''}`}>
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(leadId)}
                            onChange={() => handleSelectRow(leadId)}
                            className="h-4 w-4 text-agrosaf-green focus:ring-agrosaf-green border-gray-300 rounded cursor-pointer"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="text-sm font-medium text-gray-900 truncate capitalize">{getLeadName(lead)}</h4>
                          </div>
                          <p className="text-sm text-gray-500 truncate first-letter:capitalize">{lead.company || 'No company'}</p>
                          <p className="text-xs text-gray-500 truncate">{lead.email || 'No email'}</p>
                          <p className="text-xs text-gray-500 truncate">{lead.phone || lead.alternatePhone || 'No phone'}</p>

                          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mt-3">
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500">Source:</span>
                              <span className="text-xs font-medium text-gray-900 truncate" title={mapSourceToFrontend(lead.source)}>{mapSourceToFrontend(lead.source)}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500">Co. Source:</span>
                              <span className="text-xs font-medium text-gray-900 truncate capitalize" title={lead.companySource || 'agrosaf'}>{lead.companySource || 'agrosaf'}</span>
                            </div>
                            <div className="flex flex-col">
                              <div className="flex items-center space-x-1">
                                <span className="text-xs text-gray-500">Status:</span>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(getStageDisplayName(lead.stage))}`}>
                                  {getStageDisplayName(lead.stage)}
                                </span>
                              </div>
                              {(() => {
                                const status = getVideoVerificationStatus(lead);
                                if (status === 'approved') {
                                  return (
                                    <button
                                      onClick={() => handleViewLeadInDocuments(leadId)}
                                      className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium capitalize bg-green-100 text-green-800 hover:bg-green-200 transition-colors mt-1"
                                    >
                                      Video approved
                                    </button>
                                  );
                                }
                                if (status === 'rejected') {
                                  return (
                                    <button
                                      onClick={() => handleViewLeadInDocuments(leadId)}
                                      className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium capitalize bg-red-100 text-red-800 hover:bg-red-200 transition-colors mt-1"
                                    >
                                      Video rejected
                                    </button>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500">Temp:</span>
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold capitalize ${lead.scoreLabel === 'hot' ? 'bg-red-100 text-red-800' :
                                lead.scoreLabel === 'warm' ? 'bg-orange-100 text-orange-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                {lead.scoreLabel === 'hot' ? '🔥 Hot' :
                                  lead.scoreLabel === 'warm' ? '☀️ Warm' :
                                    '❄️ Cold'
                                }
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between mt-3">
                            <div className="text-xs text-gray-500">
                              <span className="block">Owner: <span className="first-letter:capitalize">{lead.owner || 'Unassigned'}</span></span>
                              {lead.assignmentHistory && lead.assignmentHistory.length > 0 && (
                                <span className="block text-[10px] text-gray-400">
                                  Assigned by: {getPerformerName(lead.assignmentHistory[lead.assignmentHistory.length - 1].assignedBy)}
                                </span>
                              )}
                              <span className="block">Value: ₹{lead.leadValue ? formatIndianAmount(lead.leadValue) : '0'}</span>
                              <span className="block">Created: {formatDate(lead.createdAt)}</span>
                              <span className="block">Last Activity: {calculateDaysSince(lead.lastActivityDate)}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <PermissionGate permission="lead_management" action="read">
                                <button
                                  onClick={() => handleViewLead(leadId)}
                                  className="text-blue-600 hover:text-blue-900"
                                  title="View Details"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate permission="lead_management" action="update">
                                <button
                                  onClick={() => handleEditLead(leadId)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Edit Lead"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate permission="lead_management" action="update">
                                <button
                                  onClick={() => navigate(`/log_activity/${leadId}`)}
                                  className="text-amber-600 hover:text-amber-900"
                                  title="Log Activity"
                                >
                                  <FileText className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              <PermissionGate permission="lead_management" action="create">
                                <button
                                  onClick={() => handleConvertLead(lead)}
                                  disabled={lead.stage === 'converted' || lead.stage === 'closed_won'}
                                  className={(lead.stage === 'converted' || lead.stage === 'closed_won')
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-indigo-600 hover:text-indigo-900'
                                  }
                                  title={lead.stage === 'closed_won' ? 'Won leads cannot be converted' : lead.stage === 'converted' ? 'Already Converted' : 'Convert to Opportunity'}
                                >
                                  <ArrowRight className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                              {user?.role === 'super_admin' && (
                                <PermissionGate permission="lead_management" action="delete">
                                  <button
                                    onClick={() => lead.stage !== 'converted' && lead.stage !== 'closed_won' && handleDeleteClick(leadId)}
                                    disabled={lead.stage === 'converted' || lead.stage === 'closed_won'}
                                    className={(lead.stage === 'converted' || lead.stage === 'closed_won')
                                      ? 'text-gray-400 cursor-not-allowed'
                                      : 'text-red-600 hover:text-red-900'
                                    }
                                    title={lead.stage === 'closed_won' ? 'Won leads cannot be deleted' : lead.stage === 'converted' ? 'Converted leads cannot be deleted' : 'Delete Lead'}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {
        (() => {
          const leadsTotalItems = Number(pagination?.totalLeads || leads.length || 0);
          const leadsTotalPages = Number(pagination?.totalPages || 1);
          const effectivePaginationLimit = pagination?.limit || itemsPerPage || 10;
          // Show pagination if there's more than one page
          const shouldShowPagination = !loading && leadsTotalPages > 1;
          const handleLeadsPageChange = (page) => {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          };


          return shouldShowPagination ? (
            <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                {/* Results Count and Limit Selector */}
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-700">
                    Showing {((pagination.currentPage || 1) - 1) * effectivePaginationLimit + 1} to {Math.min((pagination.currentPage || 1) * effectivePaginationLimit, leadsTotalItems)} of {leadsTotalItems} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-700">Items per page:</label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    >
                      <option value={3}>3</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleLeadsPageChange((pagination.currentPage || 1) - 1)}
                    disabled={pagination.hasPrevPage === false || (pagination.currentPage || 1) <= 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center space-x-1">
                    {(() => {
                      const totalPages = pagination.totalPages || 1;
                      const currentPageNum = pagination.currentPage || 1;
                      const pages = [];

                      if (totalPages <= 10) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push(1);

                        if (currentPageNum <= 4) {
                          for (let i = 2; i <= 5; i++) {
                            pages.push(i);
                          }
                          pages.push('ellipsis');
                          pages.push(totalPages);
                        } else if (currentPageNum >= totalPages - 3) {
                          pages.push('ellipsis');
                          for (let i = totalPages - 4; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          pages.push('ellipsis');
                          for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                            pages.push(i);
                          }
                          pages.push('ellipsis');
                          pages.push(totalPages);
                        }
                      }

                      return pages.map((page, index) => {
                        if (page === 'ellipsis') {
                          return (
                            <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                              ...
                            </span>
                          );
                        }

                        return (
                          <button
                            key={page}
                            onClick={() => handleLeadsPageChange(page)}
                            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                              ? 'bg-agrosaf-green text-white'
                              : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        );
                      });
                    })()}
                  </div>

                  <button
                    onClick={() => handleLeadsPageChange((pagination.currentPage || 1) + 1)}
                    disabled={pagination.hasNextPage === false || (pagination.currentPage || 1) >= (pagination.totalPages || 1)}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : null;
        })()
      }

      {/* Delete Confirmation Modal */}
      {
        showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                <button
                  onClick={handleCancelDelete}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete this lead? This action cannot be undone.
                </p>
              </div>
              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={handleCancelDelete}
                >
                  Cancel
                </Button>
                <PermissionGate permission="lead_management" action="delete">
                  <Button
                    variant="primary"
                    onClick={handleConfirmDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        )
      }

      {/* Bulk Delete Confirmation Modal */}
      {
        showBulkDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] !mt-0">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Confirm Bulk Delete</h3>
                <button
                  onClick={() => !isDeletingBulk && setShowBulkDeleteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isDeletingBulk}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete <strong>{selectedRows.size}</strong> leads? This action cannot be undone.
                </p>
                {isDeletingBulk && (
                  <div className="mt-4 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
                    <span className="ml-2 text-sm text-gray-600">Deleting leads...</span>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowBulkDeleteModal(false)}
                  disabled={isDeletingBulk}
                >
                  Cancel
                </Button>
                <PermissionGate permission="lead_management" action="delete">
                  <Button
                    variant="primary"
                    onClick={handleConfirmBulkDelete}
                    disabled={isDeletingBulk}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {isDeletingBulk ? 'Deleting...' : 'Delete All'}
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        )
      }

      {/* Auto Qualification Confirmation Modal */}
      {
        showAutoQualificationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Run Auto Qualification</h3>
                <button
                  onClick={() => setShowAutoQualificationModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isRunningAutoQualification}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  This will run qualification for all disqualified leads based on your active qualification criteria.
                  This process may take a while depending on the number of leads.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowAutoQualificationModal(false)}
                  disabled={isRunningAutoQualification}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  icon={<Target className="h-4 w-4" />}
                  onClick={handleRunAutoQualification}
                  disabled={isRunningAutoQualification}
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                >
                  {isRunningAutoQualification ? 'Starting...' : 'Run Qualification'}
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Auto Assignment Confirmation Modal */}
      {
        showAutoAssignmentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Run Auto Assignment</h3>
                <button
                  onClick={() => setShowAutoAssignmentModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isRunningAutoAssignment}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  This will run auto-assignment for all unassigned leads based on your active assignment rules.
                  This process may take a while depending on the number of leads.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowAutoAssignmentModal(false)}
                  disabled={isRunningAutoAssignment}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  icon={isRunningAutoAssignment ? <RefreshCw className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                  onClick={handleRunAutoAssignment}
                  disabled={isRunningAutoAssignment}
                  className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                >
                  {isRunningAutoAssignment ? 'Running...' : 'Run Assignment'}
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Bulk Assign Modal */}
      {
        showBulkAssignModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] !mt-0">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Bulk Assign Leads</h3>
                <button
                  onClick={() => !isAssigningBulk && setShowBulkAssignModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isAssigningBulk}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-4">
                  Select a user to assign <strong>{selectedRows.size}</strong> leads to:
                </p>

                <div className="relative">
                  <div className="flex items-center bg-gray-50 border border-gray-300 rounded px-3 py-2">
                    <Search className="h-4 w-4 text-gray-400 mr-2" />
                    <input
                      type="text"
                      placeholder="Search user..."
                      className="bg-transparent border-none focus:ring-0 text-sm w-full p-0"
                      value={bulkAssignSearchTerm}
                      onChange={(e) => {
                        setBulkAssignSearchTerm(e.target.value);
                        loadUsers(e.target.value, 'bulk');
                      }}
                    />
                  </div>

                  <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded">
                    {loadingUsers ? (
                      <div className="p-2 text-center text-xs text-gray-500">Loading users...</div>
                    ) : bulkAssignUsers.length > 0 ? (
                      bulkAssignUsers.map(user => (
                        <div
                          key={user.id}
                          className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${bulkAssignee === user.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                          onClick={() => setBulkAssignee(user.id)}
                        >
                          <div className="font-medium">{user.name}</div>
                          <div className="text-xs text-gray-500">{user.email} - {user.role}</div>
                        </div>
                      ))
                    ) : (
                      <div className="p-2 text-center text-xs text-gray-500">No users found</div>
                    )}
                  </div>
                </div>

                {isAssigningBulk && (
                  <div className="mt-4 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-sm text-gray-600">Assigning leads...</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowBulkAssignModal(false)}
                  disabled={isAssigningBulk}
                >
                  Cancel
                </Button>
                <PermissionGate permission="lead_management" action="update">
                  <Button
                    variant="primary"
                    onClick={handleConfirmBulkAssign}
                    disabled={isAssigningBulk || !bulkAssignee}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {isAssigningBulk ? 'Assigning...' : 'Assign All'}
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        )
      }

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={() => {
          // Refresh leads list after successful upload
          const apiFilters = buildLeadApiFilters();
          loadLeads(apiFilters);
        }}
        config={{
          title: 'Bulk Upload Leads',
          subtitle: 'Import up to 5,000 leads at a time using an Excel or CSV file',
          templateEndpoint: '/crm/leads/bulk-upload/template',
          validateEndpoint: '/crm/leads/bulk-upload/validate',
          uploadEndpoint: '/crm/leads/bulk-upload',
          templateFilename: 'lead_bulk_upload_template.xlsx',
          dataKey: 'leads',
          maxRecords: 5000,
          maxFileSizeMB: null,
          uploadChunkSize: 250,
          instructions: {
            download: [
              '1. Download the Excel template and fill in lead details.',
              <span key="max-limit"><b>2. Limit:</b> Maximum 5,000 leads can be uploaded at a time. Split larger files before uploading.</span>,
              <span key="required"><b>3. Required:</b> firstName, and at least one of: email or phone.</span>,
              <span key="assign"><b>4. Assign To:</b> (Optional) Enter Employee ID or Email to assign the lead.</span>,
              <span key="status"><b>5. Status:</b> {masterLeadStatuses.map(s => `${s.name} (${s.leadStatusCode})`).join(', ')}</span>,
              <span key="company-source"><b>6. Company Source:</b> Agrosaf, Alosafe</span>,
              <span key="region"><b>7. Region:</b> {REGIONS.join(', ')}</span>,
              <span key="temp"><b>8. Lead Temperature:</b> Hot, Warm, Cold</span>,
              <span key="timeline"><b>9. Timeline:</b> {TIMELINES.join(', ')}</span>,
              <span key="company-size"><b>10. Company Size:</b> {masterCompanySizes.length > 0 ? masterCompanySizes.map(s => `${s.name} (${s.companySizeCode})`).join(', ') : COMPANY_SIZES.join(', ')}</span>,
              <span key="category"><b>11. Category:</b> {masterCategories.length > 0 ? masterCategories.map(c => `${c.name} (${c.leadCategoryCode})`).join(', ') : 'Loading...'}</span>
            ],
            prepare: [
              <span key="industry"><b>Industry (use code):</b> {masterIndustries.filter(i => i.isActive).map(i => `${i.name} (${i.industryCode})`).join(', ') || 'Loading...'}</span>,
              <span key="decision"><b>Decision Maker (use code):</b> {masterDecisionMakers.filter(dm => dm.isActive).map(dm => `${dm.name} (${dm.decisionMakerCode})`).join(', ') || 'Loading...'}</span>,
              <span key="docs"><b>Document Types:</b> {masterDocumentTypes.filter(dt => dt.isActive).map(dt => `${dt.name} (${dt.leadDocumentTypeCode})`).join(', ') || 'Loading...'}</span>,
              <span key="lead-category"><b>Lead Category (name or code):</b> {masterCategories.map(c => `${c.name} (${c.leadCategoryCode})`).join(', ') || 'Loading...'}</span>,
              <span key="lead-source"><b>Lead Source (name or code):</b> {masterSources.filter(s => s.isActive).map(s => `${s.name} (${s.leadSourceCode})`).join(', ') || 'Loading...'}</span>,
              <span key="video"><b>Lead Video Status (name or code):</b> {masterVideoStatuses.map(s => `${s.name} (${s.leadVideoStatusCode})`).join(', ') || 'Loading...'}</span>,
              <span key="order"><b>Lead Order Status (name or code):</b> {masterOrderStatuses.map(s => `${s.name} (${s.leadOrderStatusCode})`).join(', ') || 'Loading...'}</span>,
              <span key="call"><b>Lead Call Status (name or code):</b> {masterCallStatuses.map(s => `${s.name} (${s.leadCallStatusCode})`).join(', ') || 'Loading...'}</span>,
              <span key="interest"><b>Lead Interest (name or code):</b> {masterInterests.map(s => `${s.name} (${s.leadInterestCode})`).join(', ') || 'Loading...'}</span>,
              <span key="multi-docs"><b>Multiple Docs:</b> Use semicolon (;) for multiple documentTypes/certs/urls (e.g. "GST;PAN").</span>,
              <span key="update"><b>Update Existing:</b> Provide an existing Lead ID to update the record and create an activity.</span>,
              <span key="activity"><b>New Lead Activity:</b> Provide followupType and details to create an initial activity.</span>
            ]
          }
        }}
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Leads"
        showDateRange
        scopedFormatExport={{
          onExport: handleCrmLeadsScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Assignment Rule Modal */}
      {
        showRuleModal && (
          <AssignmentRuleModal
            isOpen={showRuleModal}
            onClose={() => setShowRuleModal(false)}
            ruleToEdit={null} // Always create mode from here
            users={users}
            onSearchUsers={loadUsersForRuleModal}
            loadingUsers={loadingUsers}
            filteredUsers={filteredUsers}
            canModify={canModify}
            onSuccess={() => {
              // Rule created, strictly speaking we don't need to refresh leads list unless 
              // we want to reflect something immediately, but typically rules affect *future* assignments
              // or explicit auto-assignment runs.
              // We could fetch assignment rules count if we tracked it here, but we don't.
            }}
          />
        )
      }

      {/* Scoring Rule Modal */}
      {
        showScoringModal && (
          <ScoringRuleModal
            isOpen={showScoringModal}
            onClose={() => setShowScoringModal(false)}
            selectedScoring={selectedScoring}
            onSave={handleSaveScoring}
            isSaving={isSavingScoring}
            scoringFormData={scoringFormData}
            setScoringFormData={setScoringFormData}
            scoringErrors={scoringErrors}
            setScoringErrors={setScoringErrors}
          />
        )
      }

      {/* Qualification Criteria Modal */}
      {
        showCriteriaModal && (
          <QualificationCriteriaModal
            isOpen={showCriteriaModal}
            onClose={() => setShowCriteriaModal(false)}
            selectedCriteria={selectedCriteria}
            onSave={handleSaveCriteria}
            isSaving={isSavingCriteria}
            criteriaFormData={criteriaFormData}
            setCriteriaFormData={setCriteriaFormData}
            criteriaErrors={criteriaErrors}
            setCriteriaErrors={setCriteriaErrors}
          />
        )
      }

      {/* Convert Lead Modal */}
      <ConvertLeadModal
        isOpen={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        lead={selectedLeadForConversion}
        onSubmit={handleConvertToOpportunity}
      />

      {/* Duplicate Matches Modal */}
      {duplicateModalState.isOpen && (
        <>
          <div
            className="fixed inset-0 z-[99]"
            onClick={() => setDuplicateModalState({ isOpen: false, matches: [], sourceLeadId: null, position: null })}
          />
          <div
            className="absolute z-[100] bg-white rounded-lg shadow-xl w-80 overflow-hidden border border-gray-200 animate-in fade-in zoom-in duration-200"
            style={{
              top: `${duplicateModalState.position?.top + 8}px`,
              left: `${duplicateModalState.position?.left}px`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500" />
                Duplicate Matches
              </h3>
              <button
                onClick={() => setDuplicateModalState({ isOpen: false, matches: [], sourceLeadId: null, position: null })}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 max-h-64 overflow-y-auto custom-scrollbar">

              {duplicateModalState.matches && duplicateModalState.matches.length > 0 ? (
                <div className="space-y-2">
                  {duplicateModalState.matches.map((match, idx) => (
                    <div key={idx} className="flex justify-between items-center px-3 py-2 bg-gray-50 rounded border border-gray-100 hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-2">
                        {match.type === 'customer' ? <Users className="w-4 h-4 text-blue-500" /> : <Target className="w-4 h-4 text-orange-500" />}
                        <span className="text-xs font-medium uppercase text-gray-500">{match.type}</span>
                      </div>
                      <button
                        onClick={() => {
                          if (match.type === 'customer') {
                            if (!hasPermission('customer_management', 'read')) {
                              toast.error('Access Denied: You do not have permission to view this customer.');
                              return;
                            }
                            setDuplicateModalState({ isOpen: false, matches: [], sourceLeadId: null, position: null });
                            navigate(`/customer_360/${match.id}`);
                          } else {
                            if (isSalesScopedUser) {
                              if (match.assignedTo !== currentUserId) {
                                toast.error('Access Denied: You do not have permission to view this lead.');
                                return;
                              }
                            }
                            setDuplicateModalState({ isOpen: false, matches: [], sourceLeadId: null, position: null });
                            navigate(`/lead_detail_360/${match.id}`);
                          }
                        }}
                        className="text-sm font-semibold text-agrosaf-green hover:underline cursor-pointer"
                      >
                        {match.id}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-2">No match details available.</p>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDuplicateModalState({ isOpen: false, matches: [], sourceLeadId: null, position: null })}
              >
                Close
              </Button>
            </div>
          </div>
        </>
      )}
    </div >
  );
};

export default LeadListFilters;
